import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    Box,
    FormControl,
    LinearProgress,
    MenuItem,
    OutlinedInput,
    Paper,
    Select,
    TablePagination,
    TextField
} from '@mui/material';
import ClearAllRoundedIcon from '@mui/icons-material/ClearAllRounded';
import HandshakeRoundedIcon from '@mui/icons-material/HandshakeRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { DataGrid } from '@mui/x-data-grid';

import { Alerta } from '../../components/alerta';
import PageHeader from '../../components/pageHeader';
import { PageIntro } from '../../components/ui';
import {
    formatBackendDatePtBr,
    parseBackendDate
} from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/stringFormat';
import {
    ACORDOS_DEBOUNCED_FILTER_FIELDS,
    ACORDOS_INITIAL_FILTERS,
    ACORDOS_STATUS_OPTIONS,
    buildAcordosRows,
    fetchAcordosPage
} from './acordosData';
import '../css/biAluguelAccess.css';
import '../css/biAcordos.css';

const emptyRowsLabel = 'Nenhum acordo encontrado para os filtros aplicados.';

const getStatusToneClass = (status) => {
    const normalizedStatus = String(status || '').toUpperCase();

    if (normalizedStatus === 'ATRASADO') return 'is-overdue';
    if (normalizedStatus === 'RECEBIDO') return 'is-received';
    if (normalizedStatus === 'A VENCER') return 'is-due';
    if (normalizedStatus === 'CANCELADO') return 'is-canceled';
    return 'is-neutral';
};

const selectMenuProps = {
    PaperProps: { className: 'crm-dashboard-select-menu-paper' },
    MenuListProps: { className: 'crm-dashboard-select-menu-list' }
};

const AcordosFilters = ({ filters, onFilterChange, onClearFilters }) => (
    <div className='bi-ge-acordos-filters' role='group' aria-label='Filtros de acordos'>
        <FormControl size='small' className='bi-ge-acordos-status-filter'>
            <Select
                multiple
                displayEmpty
                value={filters.status}
                input={<OutlinedInput />}
                IconComponent={KeyboardArrowDownRoundedIcon}
                MenuProps={selectMenuProps}
                renderValue={(selected) => selected.length ? selected.join(', ') : 'Status'}
                onChange={(event) => onFilterChange('status', event.target.value)}
                inputProps={{ 'aria-label': 'Filtrar por status' }}
            >
                {ACORDOS_STATUS_OPTIONS.map((status) => (
                    <MenuItem className='crm-dashboard-select-menu-item' key={status} value={status}>
                        {status}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>

        <TextField
            size='small'
            value={filters.razao_social}
            onChange={(event) => onFilterChange('razao_social', event.target.value)}
            placeholder='Razão social'
            inputProps={{ 'aria-label': 'Filtrar por razão social' }}
            className='bi-ge-acordos-text-filter'
        />

        <TextField
            size='small'
            value={filters.documento}
            onChange={(event) => onFilterChange('documento', event.target.value)}
            placeholder='Documento'
            inputProps={{ 'aria-label': 'Filtrar por documento' }}
            className='bi-ge-acordos-text-filter'
        />

        <button
            type='button'
            className='bi-ge-acordos-clear-button'
            aria-label='Limpar filtros'
            title='Limpar filtros'
            onClick={onClearFilters}
        >
            <ClearAllRoundedIcon fontSize='inherit' />
        </button>
    </div>
);

const AcordosBIGE = () => {
    const [filters, setFilters] = useState(ACORDOS_INITIAL_FILTERS);
    const [debouncedFilters, setDebouncedFilters] = useState(ACORDOS_INITIAL_FILTERS);
    const [rows, setRows] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(100);
    const [isLoading, setIsLoading] = useState(true);
    const [isFetching, setIsFetching] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [message, setMessage] = useState('');
    const [statusAlerta, setStatusAlerta] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const hasMountedDebounce = useRef(false);

    useEffect(() => {
        if (!hasMountedDebounce.current) {
            hasMountedDebounce.current = true;
            return undefined;
        }

        const timeoutId = window.setTimeout(() => {
            setDebouncedFilters((prev) => ACORDOS_DEBOUNCED_FILTER_FIELDS.reduce((nextFilters, field) => ({
                ...nextFilters,
                [field]: filters[field]
            }), { ...prev }));
        }, 800);

        return () => window.clearTimeout(timeoutId);
    }, [filters.documento, filters.razao_social]);

    const loadAcordos = useCallback(async () => {
        setIsLoading(!hasLoaded);
        setIsFetching(hasLoaded);

        try {
            const data = await fetchAcordosPage({
                filters: debouncedFilters,
                page,
                pageSize
            });

            setRows(buildAcordosRows(data.items));
            setTotalRows(data.total);
            setPage(data.page);
            setHasLoaded(true);
        } catch (error) {
            setMessage(error.message);
            setStatusAlerta('error');
            setOpenAlerta(true);
        } finally {
            setIsLoading(false);
            setIsFetching(false);
        }
    }, [debouncedFilters, hasLoaded, page, pageSize]);

    useEffect(() => {
        loadAcordos();
    }, [loadAcordos]);

    const handleFilterChange = (field, value) => {
        setPage(1);
        setFilters((prev) => ({ ...prev, [field]: value }));

        if (field === 'status') {
            setDebouncedFilters((prev) => ({ ...prev, status: value }));
        }
    };

    const handleClearFilters = () => {
        setFilters(ACORDOS_INITIAL_FILTERS);
        setDebouncedFilters(ACORDOS_INITIAL_FILTERS);
        setPage(1);
    };

    const columns = useMemo(() => [
        {
            field: 'razao_social',
            headerName: 'Razão Social',
            minWidth: 320,
            flex: 1.45
        },
        {
            field: 'documento',
            headerName: 'Documento',
            minWidth: 170,
            flex: 0.65
        },
        {
            field: 'valor',
            headerName: 'Valor',
            type: 'number',
            minWidth: 150,
            flex: 0.45,
            valueFormatter: (value) => formatCurrency(value)
        },
        {
            field: 'num_parcela',
            headerName: '#',
            type: 'number',
            width: 88,
            align: 'center',
            headerAlign: 'center'
        },
        {
            field: 'vencimento',
            headerName: 'Vencimento',
            minWidth: 150,
            flex: 0.45,
            valueGetter: (value) => parseBackendDate(value),
            valueFormatter: (value) => formatBackendDatePtBr(value)
        },
        {
            field: 'status',
            headerName: 'Status',
            minWidth: 150,
            flex: 0.45,
            renderCell: ({ value }) => (
                <span className={`bi-ge-acordos-status-pill ${getStatusToneClass(value)}`}>
                    {value}
                </span>
            )
        }
    ], []);

    const isPageLoading = isLoading || isFetching;

    return (
        <Box className='mainBox bi-aluguel-access-page bi-ge-acordos-page'>
            <Alerta texto={message} status={statusAlerta} open={openAlerta} handleClose={() => setOpenAlerta(false)} />
            <PageHeader>Acordos</PageHeader>

            <PageIntro eyebrow='BI Cobrança' title='Acordos'>
                Acordos firmados no período, parcelas em dia e inadimplência dos acordos.
            </PageIntro>

            <section className='bi-aluguel-access-shell bi-ge-acordos-shell'>
                {isPageLoading && <LinearProgress className='bi-aluguel-access-loading' />}

                <Paper className='bi-aluguel-access-panel bi-aluguel-access-table-panel bi-ge-acordos-panel'>
                    <div className='bi-aluguel-access-panel-heading bi-ge-acordos-heading'>
                        <div>
                            <p>BI Gestão de Energia</p>
                            <h2>Lista de acordos</h2>
                            <span>{totalRows} acordos encontrados</span>
                        </div>
                        <HandshakeRoundedIcon />
                    </div>

                    <AcordosFilters
                        filters={filters}
                        onFilterChange={handleFilterChange}
                        onClearFilters={handleClearFilters}
                    />

                    <div className='bi-aluguel-access-grid-shell bi-ge-acordos-grid-shell'>
                        <DataGrid
                            className='bi-aluguel-access-grid bi-ge-acordos-grid'
                            rows={rows}
                            columns={columns}
                            loading={isPageLoading}
                            disableRowSelectionOnClick
                            hideFooter
                            localeText={{ noRowsLabel: emptyRowsLabel }}
                        />
                    </div>

                    <div className='crm-leads-pagination-shell bi-ge-acordos-pagination-shell'>
                        <div className='crm-leads-pagination-info'>
                            <span className='crm-leads-pagination-count'>
                                Página {page} de {Math.max(1, Math.ceil(totalRows / pageSize))}
                            </span>
                        </div>
                        <TablePagination
                            component='div'
                            count={totalRows}
                            page={page - 1}
                            onPageChange={(event, newPage) => setPage(newPage + 1)}
                            rowsPerPage={pageSize}
                            onRowsPerPageChange={(event) => {
                                setPageSize(parseInt(event.target.value, 10));
                                setPage(1);
                            }}
                            rowsPerPageOptions={[25, 50, 100]}
                            labelRowsPerPage='Acordos por página'
                        />
                    </div>
                </Paper>
            </section>
        </Box>
    );
};

export default AcordosBIGE;
